import type { TRootState } from "@/reducers";
import { useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";

import type { IModal } from "@reducers/layout";
import { layoutActionCreator } from "@reducers/layout";

/**
 * useLayout hook
 * get, modify the layout redux state
 *
 * TODO: header, cart, login, toast 상태 변경 함수 추가
 */
const useLayout = () => {
  const dispatch = useDispatch();

  /** 모달 열기 */
  const openModal = useCallback(
    ({
      title,
      description = "",
      confirmButton = {
        label: "Okay",
        onClick: () => {},
      },
      cancelButton,
      children,
      closeButton,
      ...args
    }: IModal) => {
      dispatch(
        layoutActionCreator.openModal({
          title,
          description,
          cancelButton,
          confirmButton,
          children,
          closeButton,
          ...args,
        }),
      );
    },
    [],
  );

  /** 모달 닫기 */
  const closeModal = useCallback(() => {
    dispatch(layoutActionCreator.closeModal());
  }, []);

  /** 모달 Progress 설정 */
  const setModalProgress = useCallback((progress: boolean) => {
    dispatch(layoutActionCreator.setModalProgress(progress));
  }, []);

  /** 로그인 모달 열기 */
  const openLoginModal = useCallback(
    ({ redirectUrl, callback }: { redirectUrl: string; callback?: (...args: any) => void }) => {
      dispatch(layoutActionCreator.openLogin({ redirectUrl, callback: (...args) => callback?.(...args) }));
    },
    [],
  );

  /** 로그인 모달 닫기 */
  const closeLoginModal = useCallback(() => {
    dispatch(layoutActionCreator.closeLogin());
  }, []);

  const openCart = useCallback(() => {
    dispatch(layoutActionCreator.openCart());
  }, []);

  const closeCart = useCallback(() => {
    dispatch(layoutActionCreator.closeCart());
  }, []);

  const openHeader = useCallback(() => {
    dispatch(layoutActionCreator.openHeader());
  }, []);

  const closeHeader = useCallback(() => {
    dispatch(layoutActionCreator.closeHeader());
  }, []);

  const openSearch = useCallback(() => {
    dispatch(layoutActionCreator.openSearch());
  }, []);

  const closeSearch = useCallback(() => {
    dispatch(layoutActionCreator.closeSearch());
  }, []);

  const showFooter = useCallback((isShowFooter: boolean) => {
    dispatch(layoutActionCreator.showFooter(isShowFooter));
  }, []);

  /**
   * setTimeout id useRef로 저장
   * 렌더링되면서 hook 내부에 변수(const, let) 값은 초기화 됨. 따라서 변수 대신 useRef current 객체에 id 저장
   * React 컴포넌트가 다시 랜더링될 때 current 속성값 유실되지 않음.
   * (추가 참고)
   * current 속성 값을 변경해도 React 컴포넌트가 다시 랜더링 X. state 상태값은 변경되면 리렌더링
   * ref 객체 안의 값은 리액트 생명주기에 독립적
   */
  // hideToast setTimeout ID 저장
  const hideToastTimerIds = useRef<number[]>([]);
  // closeToast setTimeout ID 저장
  const closeToastTimerIds = useRef<number[]>([]);
  // 토스트 열려있는지 여부
  const isToastOpen = useSelector((state: TRootState) => Boolean(state.layout.toast.message));

  /** 토스트 열기 이벤트 debouncedCallback 적용 제거 */
  const openToast = ({ message, type }: { message: string; type: string }) => {
    /**
     * 토스트가 열려있는 상태에서 openToast 호출할 경우 처리
     */
    if (isToastOpen) {
      // closeToast 호출
      dispatch(layoutActionCreator.closeToast());

      // 이전에 선언된 hide toast setTimeOut 이벤트 초기화
      hideToastTimerIds.current.forEach((timerId) => {
        clearTimeout(timerId);
      });

      hideToastTimerIds.current = [];

      // 이전에 선언된 close toast setTimeOut 이벤트 초기화
      closeToastTimerIds.current.forEach((timerId) => {
        clearTimeout(timerId);
      });

      closeToastTimerIds.current = [];
    }

    // openToast 호출
    dispatch(layoutActionCreator.openToast({ message, toastType: type }));

    window.setTimeout(() => {
      dispatch(layoutActionCreator.closeToast());
    }, 2000);
  };

  return {
    isSearchOpen: useSelector((state: TRootState) => state.layout.isSearchOpen),
    isShowFooter: useSelector((state: TRootState) => state.layout.isShowFooter),
    isCartOpen: useSelector((state: TRootState) => state.layout.isCartOpen),
    isLoginOpen: useSelector((state: TRootState) => state.layout.isLoginOpen),
    isModalOpen: useSelector((state: TRootState) => state.layout.isModalOpen),
    isHeaderOpen: useSelector((state: TRootState) => state.layout.isHeaderOpen),
    isToastOpen: useSelector((state: TRootState) => Boolean(state.layout.toast.message)),
    modal: useSelector((state: TRootState) => state.layout.modal),
    toast: useSelector((state: TRootState) => state.layout.toast),
    loginModalProps: useSelector((state: TRootState) => state.layout.loginModalProps),
    openModal,
    closeModal,
    setModalProgress,
    openLoginModal,
    closeLoginModal,
    openCart,
    closeCart,
    showFooter,
    openHeader,
    closeHeader,
    openSearch,
    closeSearch,
    openToast,
  };
};

export default useLayout;
